/**
 * PURPOSE: Vista Por Fecha del Centro de Programación (FTR-0028 WG3, §16 / vista 2).
 * RESPONSIBILITIES: Operar una jornada completa: ver sus partidos, editarlos en línea y aplicar un
 *   cambio a toda la jornada cuando no hay selección.
 *
 *   C9 — UNA JORNADA NO ES UNA RONDA. Con grupos de distinto tamaño (`[5, 4]`) la fecha 4 es ronda 1
 *   para el grupo de 5 y ronda 2 para el de 4. La cabecera lo dice explícitamente cuando ocurre, en
 *   vez de dejar que el operador suponga una equivalencia que el sistema no tiene.
 *
 *   "APLICAR A TODA LA JORNADA" ES UNA EDICIÓN MASIVA PARCIAL (semántica FTR-0008). Fecha, hora y
 *   sede son campos INDEPENDIENTES y ninguno es obligatorio: se escribe lo que el operador completó
 *   y se conserva lo que dejó en blanco. Exigir una hora para poder mover una fecha convertía la
 *   herramienta en un formulario. Lo único obligatorio es que haya algún cambio.
 *
 *   LA HORA ENCADENA POR SEDE. La duración es solo el paso de cálculo —nunca un campo del partido—:
 *   el primer partido de cada sede arranca en la hora inicial y los siguientes suman la duración, EN
 *   PARALELO por sede, no en una secuencia global. "Sin sede" es un bloque más y encadena igual. Sin
 *   duración no hay nada que incrementar, así que todos reciben la misma hora inicial.
 *
 *   El alcance sigue siendo la jornada completa (nunca lo que el filtro de pantalla deja visible) y
 *   los BYE se excluyen igual que siempre (D-20).
 * COLLABORATORS: ProgrammingCenterProvider.jsx, MatchRow.jsx, utils/programmingCenterEdit.js.
 * ROUTE: winscore/src/area/FasesArea/ProgrammingCenter/views/DateView.jsx
 */
import React, { useMemo, useState } from "react";
import { FiAlertCircle, FiCalendar, FiCheck } from "react-icons/fi";

import { useProgrammingCenter } from "../ProgrammingCenterProvider.jsx";
import { conflictSeverity, groupByDate } from "../../../../utils/programmingCenterGrouping.js";
import { NO_VENUE } from "../../../../utils/programmingCenterEdit.js";
import { legLabel } from "../../../../utils/programmingCenterModel.js";
import MatchRow from "../components/MatchRow.jsx";

const EMPTY_JORNADA = Object.freeze({ date: "", startTime: "", stepMinutes: "60", idsede: "" });

const JornadaBar = ({ bucket }) => {
  const { context, actions, busy } = useProgrammingCenter();
  const [changes, setChanges] = useState({ ...EMPTY_JORNADA });
  const venues = context?.venues || [];

  const apply = async () => {
    const result = await actions.applyJornada({
      dateNo: bucket.dateNo,
      idtypephase: bucket.phase,
      date: changes.date,
      startTime: changes.startTime,
      stepMinutes: changes.stepMinutes,
      idsede: changes.idsede,
    });
    if (result?.ok) setChanges({ ...EMPTY_JORNADA });
  };

  const field = (name, node) => (
    <label className="flex min-w-0 flex-col gap-0.5">
      <span className="text-[10px] font-black uppercase tracking-wide text-slate-400">{name}</span>
      {node}
    </label>
  );

  const inputClass =
    "rounded-lg border border-slate-300 bg-white px-2 py-1 text-sm font-semibold text-slate-800 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100";

  return (
    <div className="flex flex-col gap-1 bg-blue-50/60 px-3 py-2">
      <div className="flex flex-wrap items-end gap-2">
        <span className="mr-1 flex items-center gap-1 text-[11px] font-black uppercase tracking-wide text-blue-700">
          <FiCalendar className="text-xs" />
          Aplicar a toda la jornada
        </span>
        {field(
          "Fecha",
          <input
            type="date"
            value={changes.date}
            onChange={(event) => setChanges((c) => ({ ...c, date: event.target.value }))}
            className={inputClass}
            aria-label={`Fecha para la jornada ${bucket.dateNo}`}
          />
        )}
        {field(
          "Hora inicial",
          <input
            type="time"
            value={changes.startTime}
            onChange={(event) => setChanges((c) => ({ ...c, startTime: event.target.value }))}
            className={inputClass}
            aria-label={`Hora inicial para la jornada ${bucket.dateNo}`}
          />
        )}
        {field(
          "Duración estimada (min)",
          <input
            type="number"
            min="0"
            step="5"
            value={changes.stepMinutes}
            onChange={(event) => setChanges((c) => ({ ...c, stepMinutes: event.target.value }))}
            className={inputClass}
            aria-label={`Duración estimada para la jornada ${bucket.dateNo}`}
          />
        )}
        {field(
          "Sede",
          <select
            value={changes.idsede}
            onChange={(event) => setChanges((c) => ({ ...c, idsede: event.target.value }))}
            className={inputClass}
            aria-label={`Sede para la jornada ${bucket.dateNo}`}
          >
            <option value="">Sin cambio</option>
            {venues.map((venue) => (
              <option key={venue.idsede} value={venue.idsede}>
                {venue.name}
              </option>
            ))}
            <option value={NO_VENUE}>Sin sede</option>
          </select>
        )}
        <button
          type="button"
          onClick={apply}
          disabled={Boolean(busy)}
          className="flex items-center gap-1 rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-black text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <FiCheck className="text-sm" />
          Aplicar
        </button>
      </div>
      <p className="text-[11px] font-bold text-slate-500">
        Cada campo es independiente: se aplica solo lo que completes y lo demás se conserva. La
        duración únicamente calcula la secuencia de horas dentro de cada sede —incluida «sin
        sede»— y no se guarda en el partido.
      </p>
    </div>
  );
};

const DateView = () => {
  const {
    visibleRows,
    selection,
    toggleSelection,
    setBlockSelection,
    conflictsByKey,
    scheduleDraft,
    setScheduleDraft,
    courtsByVenue,
    context,
    busy,
    actions,
  } = useProgrammingCenter();

  const buckets = useMemo(() => groupByDate(visibleRows), [visibleRows]);
  const venues = context?.venues || [];

  const onScheduleChange = (row, field, value) =>
    setScheduleDraft((current) =>
      actions.changeScheduleField(current, row, field, value)
    );

  if (!buckets.length) {
    return (
      <p className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm font-bold text-slate-500">
        No hay partidos que coincidan con los filtros actuales.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {buckets.map((bucket) => {
        const selectable = bucket.rows.filter((row) => row.canSelect);
        const allSelected =
          selectable.length > 0 && selectable.every((row) => selection.has(row.iddraftmatch));

        return (
          <section
            key={bucket.key}
            className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm"
          >
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 px-3 py-2">
              <div className="flex items-center gap-3">
                {selectable.length ? (
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={(event) => setBlockSelection(selectable, event.target.checked)}
                    aria-label={`Seleccionar ${bucket.label}`}
                    className="h-4 w-4 cursor-pointer rounded border-slate-300 text-blue-600 focus:ring-blue-400"
                  />
                ) : null}
                <div>
                  <p className="text-sm font-black text-slate-900">{bucket.label}</p>
                  <p className="text-[11px] font-bold text-slate-500">
                    {bucket.rows.filter((row) => !row.isBye).length} partidos ·{" "}
                    {bucket.groups.length} {bucket.groups.length === 1 ? "grupo" : "grupos"}
                  </p>
                </div>
              </div>
              {bucket.mixedLegs ? (
                <span
                  className="flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-1 text-[11px] font-black text-amber-700"
                  title="Los grupos de distinto tamaño no comparten numeración de fechas: esta jornada contiene rondas distintas según el grupo."
                >
                  <FiAlertCircle className="text-xs" />
                  Rondas mixtas: {bucket.legs.map((leg) => legLabel(leg)).join(" + ")}
                </span>
              ) : bucket.legs.length === 1 ? (
                <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-1 text-[11px] font-black text-slate-500">
                  {legLabel(bucket.legs[0])}
                </span>
              ) : null}
            </div>

            {bucket.phase === 1 ? <JornadaBar bucket={bucket} /> : null}

            {bucket.groups.map((group) => (
              <div key={`${bucket.key}-${group.groupNo || "none"}`} className="border-b border-slate-100 last:border-b-0">
                <div className="bg-slate-50/80 px-3 py-1.5">
                  <span className="text-[11px] font-black uppercase tracking-wide text-slate-500">
                    {group.label}
                  </span>
                </div>
                <div className="flex flex-col gap-1.5 p-3">
                  {group.rows.map((row) => {
                    const conflicts = conflictsByKey.get(row.draftKey) || [];
                    return (
                      <MatchRow
                        key={row.key}
                        row={row}
                        layout="date"
                        selected={selection.has(row.iddraftmatch)}
                        onToggle={toggleSelection}
                        conflicts={conflicts}
                        severity={conflictSeverity(conflicts)}
                        entry={scheduleDraft[row.iddraftmatch]}
                        venues={venues}
                        courtsByVenue={courtsByVenue}
                        onScheduleChange={onScheduleChange}
                        disabled={Boolean(busy)}
                      />
                    );
                  })}
                </div>
              </div>
            ))}
          </section>
        );
      })}
    </div>
  );
};

export default DateView;
